import * as ents from '@attain-apps/entities'
import { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'
import { XMLParser } from 'fast-xml-parser'

import type { Data } from './data'
import type { Edge } from './edge'
import type { Chart } from './org-chart'

/**
 * Assistant-authored org-chart proposal data rendered when a proposal button is
 * clicked in chat history.
 *
 * This model supports two XML contracts:
 *
 * ```txt
 * legacy snapshot XML: <nodes> + <edges>
 *   -> proposal edges are the explicit proposed structure
 *
 * delta XML: <nodes> + <edge-changes>
 *   -> proposal edges are applied to the current chart at click time
 * ```
 *
 * In both modes, existing current-chart nodes omitted from {@link Proposal.nodes}
 * are carried forward unchanged. Delta mode also preserves every omitted
 * reporting relationship, so the assistant only needs to describe intended
 * changes instead of re-sending the whole chart.
 */
export interface Proposal {
  readonly edgeChanges: readonly Proposal.EdgeChange[]
  readonly edges: readonly Edge[]
  readonly mode: Proposal.Mode
  readonly nodes: readonly Proposal.Node[]
}
export namespace Proposal {
  export type Mode = 'delta' | 'snapshot'

  /**
   * Minimal employee payload accepted from assistant proposal XML.
   *
   * The proposal flow only relies on stable identity plus display text. Richer
   * runtime node values are derived later when the proposal is materialized
   * into tree-editor {@link Data}.
   */
  export interface Node {
    id: ents.core.display.tree.Node.Id
    name: string
    description: string
  }

  /**
   * Delta-mode reporting relationship change.
   *
   * A non-null {@link parentId} adds or replaces the child's typed reporting
   * line. A null {@link parentId} removes that typed reporting line, leaving
   * unrelated relationships unchanged.
   */
  export interface EdgeChange {
    parentId: ents.core.display.tree.Node.Id | null
    childId: ents.core.display.tree.Node.Id
    leaderType?: ents.extra.common.LeaderType
  }

  type NormalizedEdge = Edge & {
    leaderType: ents.extra.common.LeaderType
  }

  type NormalizedEdgeChange = Omit<EdgeChange, 'leaderType'> & {
    leaderType: ents.extra.common.LeaderType
  }

  type EdgeMaterializationResult =
    | {
        ok: true
        data: readonly NormalizedEdge[]
      }
    | {
        ok: false
        error: string
      }

  /**
   * Result of decoding assistant XML into a validated {@link Proposal}.
   *
   * Errors returned here are intended to be shown back through the feature
   * state, so the messages should describe what is wrong with the assistant
   * payload rather than expose parser internals.
   */
  export type ParseResult =
    | {
        ok: true
        data: Proposal
      }
    | {
        ok: false
        error: string
      }

  /**
   * Result of materializing a {@link Proposal} into tree-editor {@link Data}.
   *
   * This stage validates proposal semantics that only make sense once the
   * current chart is available, such as current-node preservation, edge
   * references, and reuse of existing runtime metadata.
   */
  export type ToDataResult =
    | {
        ok: true
        data: Data
      }
    | {
        ok: false
        error: string
      }

  /**
   * Decodes assistant proposal XML into a validated {@link Proposal}.
   *
   * The assistant response may wrap the proposal in the markdown extension's
   * `<atn-org-chart>` element. New proposal XML uses `<edge-changes>`; legacy
   * history may still contain `<edges>`.
   */
  export function fromXml(xml: string): ParseResult {
    try {
      const xmlParser = new XMLParser({
        attributeNamePrefix: '',
        ignoreAttributes: false,
      })
      const parsed: unknown = xmlParser.parse(xml) as unknown
      const root = getProposalRoot(parsed)
      const nodesWrapper = asOptionalXmlObject(root.nodes, 'nodes')
      const nodes = deduplicateNodes(
        normalizeList(nodesWrapper?.node).map(parseNode),
      )

      if (hasOwnProperty(root, 'edge-changes')) {
        const edgeChangesWrapper = asOptionalXmlObject(
          root['edge-changes'],
          'edge-changes',
        )
        return {
          data: {
            edgeChanges: [
              ...normalizeList(edgeChangesWrapper?.edge).map(parseEdgeChange),
              ...normalizeList(edgeChangesWrapper?.remove).map(
                parseEdgeRemoval,
              ),
            ],
            edges: [],
            mode: 'delta',
            nodes,
          },
          ok: true,
        }
      }

      const edgesWrapper = asOptionalXmlObject(root.edges, 'edges')
      return {
        data: {
          edgeChanges: [],
          edges: normalizeList(edgesWrapper?.edge).map(parseEdge),
          mode: 'snapshot',
          nodes,
        },
        ok: true,
      }
    } catch (error) {
      return {
        error:
          error instanceof Error
            ? error.message
            : `Unable to parse proposal XML: ${String(error)}`,
        ok: false,
      }
    }
  }

  /**
   * Builds the tree-editor {@link Data} used by the assistant proposal button.
   *
   * Workflow:
   *
   *   proposal XML
   *     -> merge assistant-authored nodes with omitted current-chart nodes
   *     -> patch existing runtime nodes or create new ones
   *     -> materialize either snapshot edges or delta edge changes
   *     -> carry forward compatible edge metadata from the current chart
   *
   * Validation here is intentionally strict because the proposal button creates
   * a chart directly from this output. Catching malformed structure here avoids
   * partial tree state and harder-to-debug rendering failures later.
   */
  export function toData({
    currentChart,
    proposal,
  }: {
    currentChart: Chart
    proposal: Proposal
  }): ToDataResult {
    const currentNodesById = new Map<
      ents.core.display.tree.Node.Id,
      ents.core.display.tree.Node
    >()
    for (const currentNode of currentChart.nodes) {
      currentNodesById.set(
        ents.core.display.tree.Node.Id.get(currentNode),
        currentNode,
      )
    }

    const proposalNodesById = new Map<ents.core.display.tree.Node.Id, Node>()
    for (const proposalNode of proposal.nodes) {
      if (proposalNodesById.has(proposalNode.id)) {
        return {
          error: `Proposal XML contains duplicate node '${proposalNode.id}'.`,
          ok: false,
        }
      }

      proposalNodesById.set(proposalNode.id, proposalNode)
    }

    const finalNodeIds = new Set<ents.core.display.tree.Node.Id>([
      ...currentNodesById.keys(),
      ...proposalNodesById.keys(),
    ])

    const edgeMaterialization =
      proposal.mode === 'delta'
        ? materializeDeltaEdges({ currentChart, proposal })
        : materializeSnapshotEdges({
            currentChart,
            proposal,
            proposalNodesById,
          })
    if (!edgeMaterialization.ok) {
      return edgeMaterialization
    }

    const edgeValidation = validateMaterializedEdges({
      edges: edgeMaterialization.data,
      finalNodeIds,
    })
    if (!edgeValidation.ok) {
      return edgeValidation
    }

    const currentEdgeByKey = new Map<string, NormalizedEdge>()
    for (const currentEdge of currentChart.edges) {
      const normalizedCurrentEdge = normalizeEdgeLeaderType(currentEdge)
      currentEdgeByKey.set(
        edgeKey(normalizedCurrentEdge),
        normalizedCurrentEdge,
      )
    }

    const newProposalNodes = proposal.nodes.filter(
      proposalNode => !currentNodesById.has(proposalNode.id),
    )
    const newNodes: ents.core.display.tree.Node[] = []
    if (newProposalNodes.length > 0) {
      const defaultModelId =
        currentChart.metadata.defaultModelId ??
        getMostCommonModelId(currentChart)
      if (!defaultModelId) {
        return {
          error: 'Unable to determine a modelId for assistant-created nodes.',
          ok: false,
        }
      }

      newNodes.push(
        ...newProposalNodes.map(proposalNode =>
          createNewNode({ modelId: defaultModelId, proposalNode }),
        ),
      )
    }

    return {
      data: {
        edges: edgeMaterialization.data.map(edge => {
          const matchingCurrentEdge = currentEdgeByKey.get(edgeKey(edge))
          return {
            ...edge,
            leaderType: edge.leaderType,
            recordId: matchingCurrentEdge?.recordId ?? edge.recordId,
          }
        }),
        nodes: [
          ...currentChart.nodes.map(currentNode => {
            const nodeId = ents.core.display.tree.Node.Id.get(currentNode)
            const proposalNode = proposalNodesById.get(nodeId)
            // Existing employees keep their runtime shape and only receive the
            // display fields the assistant is allowed to change. Omitted
            // existing employees are carried forward unchanged.
            return proposalNode
              ? patchExistingNode({ currentNode, proposalNode })
              : currentNode
          }),
          ...newNodes,
        ],
      },
      ok: true,
    }
  }

  /**
   * Materializes legacy snapshot XML where proposal edges represent the
   * proposed structure authored by the assistant.
   */
  function materializeSnapshotEdges({
    currentChart,
    proposal,
    proposalNodesById,
  }: {
    currentChart: Chart
    proposal: Proposal
    proposalNodesById: ReadonlyMap<ents.core.display.tree.Node.Id, Node>
  }): EdgeMaterializationResult {
    const proposalEdges = proposal.edges.map(normalizeEdgeLeaderType)
    const proposalRelationshipKeys = new Set(
      proposalEdges.map(edgeRelationshipKey),
    )

    const currentOnlyNodeIds = new Set(
      currentChart.nodes
        .map(node => ents.core.display.tree.Node.Id.get(node))
        .filter(nodeId => !proposalNodesById.has(nodeId)),
    )

    return {
      data: [
        ...proposalEdges,
        ...currentChart.edges
          .map(normalizeEdgeLeaderType)
          .filter(
            currentEdge =>
              currentOnlyNodeIds.has(currentEdge.childId) &&
              !proposalRelationshipKeys.has(edgeRelationshipKey(currentEdge)),
          ),
      ],
      ok: true,
    }
  }

  /**
   * Materializes delta XML by applying changed relationships to the current
   * chart while preserving every omitted relationship.
   */
  function materializeDeltaEdges({
    currentChart,
    proposal,
  }: {
    currentChart: Chart
    proposal: Proposal
  }): EdgeMaterializationResult {
    const currentEdges = currentChart.edges.map(normalizeEdgeLeaderType)
    const edgeChanges = proposal.edgeChanges.map(normalizeEdgeChangeLeaderType)

    const duplicateEdgeChangeRelationshipKeys = getDuplicateValues(
      edgeChanges.map(edgeRelationshipKey),
    )
    if (duplicateEdgeChangeRelationshipKeys.length > 0) {
      return {
        error:
          'Proposal XML contains multiple changes for child/leaderType pairs: ' +
          `${formatRelationshipKeys(duplicateEdgeChangeRelationshipKeys)}.`,
        ok: false,
      }
    }

    for (const change of edgeChanges) {
      if (change.parentId !== null && change.childId === change.parentId) {
        return {
          error: `Proposal XML would make node '${change.childId}' report to itself.`,
          ok: false,
        }
      }
    }

    const currentEdgeRelationshipKeys = new Set(
      currentEdges.map(edgeRelationshipKey),
    )
    const finalEdgesByRelationshipKey = new Map<string, NormalizedEdge>()
    for (const edge of currentEdges) {
      finalEdgesByRelationshipKey.set(edgeRelationshipKey(edge), edge)
    }

    for (const change of edgeChanges) {
      if (change.parentId === null) {
        finalEdgesByRelationshipKey.delete(edgeRelationshipKey(change))
        continue
      }

      finalEdgesByRelationshipKey.set(edgeRelationshipKey(change), {
        childId: change.childId,
        leaderType: change.leaderType,
        parentId: change.parentId,
      })
    }

    return {
      data: [
        ...currentEdges
          .map(edge =>
            finalEdgesByRelationshipKey.get(edgeRelationshipKey(edge)),
          )
          .filter((edge): edge is NormalizedEdge => edge !== undefined),
        ...edgeChanges
          .filter(
            change =>
              change.parentId !== null &&
              !currentEdgeRelationshipKeys.has(edgeRelationshipKey(change)),
          )
          .map(change =>
            finalEdgesByRelationshipKey.get(edgeRelationshipKey(change)),
          )
          .filter((edge): edge is NormalizedEdge => edge !== undefined),
      ],
      ok: true,
    }
  }

  /**
   * Validates materialized edges after proposal mode-specific merge rules have
   * produced the final proposed relationship set.
   */
  function validateMaterializedEdges({
    edges,
    finalNodeIds,
  }: {
    edges: readonly NormalizedEdge[]
    finalNodeIds: ReadonlySet<ents.core.display.tree.Node.Id>
  }): EdgeMaterializationResult {
    const edgeKeys = new Set<string>()
    const relationshipKeys = new Set<string>()
    const primaryParentByChild = new Map<
      ents.core.display.tree.Node.Id,
      ents.core.display.tree.Node.Id
    >()

    for (const edge of edges) {
      // All edge endpoints must resolve within the merged proposed chart so
      // the renderer and approval flow never see dangling relationships.
      if (!finalNodeIds.has(edge.parentId)) {
        return {
          error:
            `Proposal edge '${edge.parentId}->${edge.childId}' references ` +
            `missing parent '${edge.parentId}'.`,
          ok: false,
        }
      }

      if (!finalNodeIds.has(edge.childId)) {
        return {
          error:
            `Proposal edge '${edge.parentId}->${edge.childId}' references ` +
            `missing child '${edge.childId}'.`,
          ok: false,
        }
      }

      if (edgeKeys.has(edgeKey(edge))) {
        return {
          error:
            `Proposal XML contains duplicate edge ` +
            `'${edge.parentId}->${edge.childId}'.`,
          ok: false,
        }
      }

      edgeKeys.add(edgeKey(edge))

      const relationshipKey = edgeRelationshipKey(edge)
      if (relationshipKeys.has(relationshipKey)) {
        return {
          error:
            `Proposal XML assigns multiple '${edge.leaderType}' parents ` +
            `to '${edge.childId}'.`,
          ok: false,
        }
      }

      relationshipKeys.add(relationshipKey)
      if (edge.leaderType === ents.extra.common.LeaderType.Manager) {
        primaryParentByChild.set(edge.childId, edge.parentId)
      }
    }

    const cycle = findCycle({
      edgesByChildId: primaryParentByChild,
      nodeIds: Array.from(finalNodeIds),
    })
    if (cycle !== undefined) {
      return {
        error:
          'Proposal XML creates a reporting cycle involving ' +
          `'${cycle[0]}'.`,
        ok: false,
      }
    }

    return { data: edges, ok: true }
  }

  /**
   * Unwraps the optional markdown-extension wrapper around proposal XML.
   *
   * The assistant markdown button stores the proposal inside
   * `<atn-org-chart>`, but the underlying parsing logic also accepts the raw
   * inner structure so the model can be used independently.
   */
  function getProposalRoot(parsed: unknown): Record<string, unknown> {
    if (!TypeChecker.isObject(parsed) || Array.isArray(parsed)) {
      throw new Error('Proposal XML did not decode into an object.')
    }

    const root = parsed as Record<string, unknown>
    const wrapper = root['atn-org-chart']
    if (TypeChecker.isObject(wrapper) && !Array.isArray(wrapper)) {
      return wrapper as Record<string, unknown>
    }

    return root
  }

  /**
   * Normalizes singular XML entries into the repeated-list shape used
   * everywhere else in the proposal pipeline.
   */
  function normalizeList<T>(value: T | readonly T[] | undefined): readonly T[] {
    if (value === undefined) {
      return []
    }

    if (Array.isArray(value)) {
      return value
    }

    return [value as T]
  }

  /**
   * Keeps assistant proposal XML tolerant of redundant node declarations while
   * preserving the one-node-per-id invariant required by the tree editor.
   */
  function deduplicateNodes(
    nodes: readonly Proposal.Node[],
  ): readonly Proposal.Node[] {
    const seenNodeIds = new Set<ents.core.display.tree.Node.Id>()
    const uniqueNodes: Proposal.Node[] = []

    for (const node of nodes) {
      if (seenNodeIds.has(node.id)) {
        continue
      }

      seenNodeIds.add(node.id)
      uniqueNodes.push(node)
    }

    return uniqueNodes
  }

  /**
   * Parses one XML node entry into the minimal {@link Proposal.Node} shape.
   *
   * The assistant only owns id/name/description for proposal nodes. All other
   * runtime fields are recovered from the current chart or synthesized later.
   */
  function parseNode(value: unknown): Proposal.Node {
    const node = asXmlObject(value, 'node')
    const id = parseNodeId(node.id, 'id', 'node')
    const name = node.name
    const description = node.description

    if (!TypeChecker.isString(name) || name.trim().length === 0) {
      throw new Error(`Proposal node '${id}' must include a non-empty name.`)
    }
    if (!TypeChecker.isString(description)) {
      throw new Error(
        `Proposal node '${id}' must include a string description.`,
      )
    }

    return {
      description: description.trim(),
      id,
      name: name.trim(),
    }
  }

  /**
   * Parses one legacy snapshot XML edge entry into the tree-editor
   * {@link Edge} shape.
   */
  function parseEdge(value: unknown): Edge {
    const edge = asXmlObject(value, 'edge')
    return {
      childId: parseNodeId(edge.childId, 'childId', 'edge'),
      leaderType: parseLeaderType(edge.leaderType, 'edge'),
      parentId: parseNodeId(edge.parentId, 'parentId', 'edge'),
    }
  }

  /** Parses one delta XML edge-change entry. */
  function parseEdgeChange(value: unknown): EdgeChange {
    const edge = parseEdge(value)
    return {
      childId: edge.childId,
      leaderType: edge.leaderType,
      parentId: edge.parentId,
    }
  }

  /** Parses one delta XML remove entry. */
  function parseEdgeRemoval(value: unknown): EdgeChange {
    const edgeRemoval = asXmlObject(value, 'remove')
    return {
      childId: parseNodeId(edgeRemoval.childId, 'childId', 'remove'),
      leaderType: parseLeaderType(edgeRemoval.leaderType, 'remove'),
      parentId: null,
    }
  }

  /**
   * Validates that a parsed XML entry is an object of the expected kind before
   * field-level parsing runs.
   */
  function asXmlObject(
    value: unknown,
    kind: 'node' | 'edge' | 'remove',
  ): Record<string, unknown> {
    if (!TypeChecker.isObject(value) || Array.isArray(value)) {
      throw new Error(`Proposal ${kind} entry must be an object.`)
    }

    return value as Record<string, unknown>
  }

  /**
   * Reads wrapper elements while tolerating empty wrappers emitted by the
   * proposal tool, such as `<nodes></nodes>`.
   */
  function asOptionalXmlObject(
    value: unknown,
    kind: 'edge-changes' | 'edges' | 'nodes',
  ): Record<string, unknown> | undefined {
    if (value === undefined || value === '') {
      return undefined
    }

    if (!TypeChecker.isObject(value) || Array.isArray(value)) {
      throw new Error(`Proposal ${kind} wrapper must be an object.`)
    }

    return value as Record<string, unknown>
  }

  /** Parses node ids while accepting plain string IDs from XML attributes. */
  function parseNodeId(
    value: unknown,
    fieldName: 'childId' | 'id' | 'parentId',
    kind: 'edge' | 'node' | 'remove',
  ): ents.core.display.tree.Node.Id {
    let id = value
    if (!PartialId.isPartialId(id) && TypeChecker.isString(id)) {
      id = PartialId.from(id)
    }

    if (!ents.core.display.tree.Node.Id.isId(id)) {
      throw new Error(
        `Proposal ${kind} has invalid ${fieldName}: ${String(id)}`,
      )
    }

    return id
  }

  /** Parses missing relationship types as the primary manager relationship. */
  function parseLeaderType(
    value: unknown,
    kind: 'edge' | 'remove',
  ): ents.extra.common.LeaderType {
    if (value === undefined) {
      return ents.extra.common.LeaderType.Manager
    }

    if (!ents.extra.common.LeaderType.isLeaderType(value)) {
      throw new Error(`Proposal ${kind} has invalid leaderType.`)
    }

    return value
  }

  /** Checks whether an XML wrapper was present, even if it parsed as empty. */
  function hasOwnProperty(
    value: Record<string, unknown>,
    key: string,
  ): boolean {
    return Object.prototype.hasOwnProperty.call(value, key)
  }

  /**
   * Applies assistant-authored display fields onto an existing runtime node
   * while preserving unrelated runtime values owned by the current chart.
   *
   * Existing employees keep their current record-backed shape. The assistant
   * only updates the display fields that are surfaced in the proposed chart.
   */
  function patchExistingNode({
    currentNode,
    proposalNode,
  }: {
    currentNode: ents.core.display.tree.Node
    proposalNode: Proposal.Node
  }): ents.core.display.tree.Node {
    return {
      ...currentNode,
      label: proposalNode.name,
      values: {
        ...currentNode.values,
        [ents.core.dms.data.Record.Id.KEY]:
          currentNode.values?.[ents.core.dms.data.Record.Id.KEY] ??
          proposalNode.id,
        '!descr': proposalNode.description,
        '!name': proposalNode.name,
      },
    }
  }

  /**
   * Creates a valid runtime node for a newly proposed employee using safe
   * defaults for required tree-editor values that the assistant does not own.
   *
   * These defaults exist only so the tree editor can render and persist the new
   * employee consistently; proposal XML stays intentionally minimal.
   */
  function createNewNode({
    modelId,
    proposalNode,
  }: {
    modelId: ents.core.dms.models.ForRecord.Id
    proposalNode: Proposal.Node
  }): ents.core.display.tree.Node {
    return {
      label: proposalNode.name,
      modelId,
      values: {
        [ents.core.dms.data.Record.Id.KEY]: proposalNode.id,
        '!active_periods': '{}',
        '!code': proposalNode.id,
        '!descr': proposalNode.description,
        '!name': proposalNode.name,
      },
    }
  }

  /**
   * Chooses a fallback record model for assistant-created nodes when the chart
   * metadata does not already declare one.
   *
   * New employees should generally follow the dominant employee model already
   * present in the current chart unless the chart metadata provides a more
   * explicit default.
   */
  function getMostCommonModelId(
    currentChart: Chart,
  ): ents.core.dms.models.ForRecord.Id | undefined {
    const counts = new Map<ents.core.dms.models.ForRecord.Id, number>()

    for (const node of currentChart.nodes) {
      counts.set(node.modelId, (counts.get(node.modelId) ?? 0) + 1)
    }

    let modelId: ents.core.dms.models.ForRecord.Id | undefined
    let maxCount = 0
    for (const [candidateModelId, count] of counts.entries()) {
      if (count > maxCount) {
        maxCount = count
        modelId = candidateModelId
      }
    }

    return modelId
  }

  /** Returns values that appear more than once, preserving first duplicate order. */
  function getDuplicateValues(values: readonly string[]): string[] {
    const duplicates = new Set<string>()
    const seen = new Set<string>()

    for (const value of values) {
      if (seen.has(value)) {
        duplicates.add(value)
      } else {
        seen.add(value)
      }
    }

    return Array.from(duplicates)
  }

  /** Formats child/leaderType edge relationship identities for error messages. */
  function formatRelationshipKeys(keys: readonly string[]): string {
    return keys
      .map(key => {
        const [childId, leaderType] = key.split('::')
        return `'${childId}' (${leaderType})`
      })
      .join(', ')
  }

  /** Finds the first reporting cycle by walking each child-to-parent chain. */
  function findCycle(params: {
    edgesByChildId: ReadonlyMap<
      ents.core.display.tree.Node.Id,
      ents.core.display.tree.Node.Id
    >
    nodeIds: readonly ents.core.display.tree.Node.Id[]
  }): readonly ents.core.display.tree.Node.Id[] | undefined {
    const { edgesByChildId, nodeIds } = params
    const path: ents.core.display.tree.Node.Id[] = []
    const stateById = new Map<
      ents.core.display.tree.Node.Id,
      'visited' | 'visiting'
    >()

    /** Visits one node in the child-to-parent graph. */
    function visit(
      id: ents.core.display.tree.Node.Id,
    ): readonly ents.core.display.tree.Node.Id[] | undefined {
      const state = stateById.get(id)
      if (state === 'visited') {
        return undefined
      }
      if (state === 'visiting') {
        const cycleStart = path.indexOf(id)
        return [...path.slice(cycleStart), id]
      }

      stateById.set(id, 'visiting')
      path.push(id)

      const parentId = edgesByChildId.get(id)
      const cycle = parentId === undefined ? undefined : visit(parentId)
      if (cycle !== undefined) {
        return cycle
      }

      path.pop()
      stateById.set(id, 'visited')
      return undefined
    }

    for (const nodeId of nodeIds) {
      const cycle = visit(nodeId)
      if (cycle !== undefined) {
        return cycle
      }
    }

    return undefined
  }

  /**
   * Produces a stable lookup key for matching proposal and current edges when
   * carrying forward runtime metadata.
   */
  function edgeKey(edge: Pick<Edge, 'parentId' | 'childId'>): string {
    return `${edge.parentId}::${edge.childId}`
  }

  /**
   * Treats omitted leaderType values as primary manager relationships so
   * proposal XML can target typed relationships deterministically.
   */
  function normalizeEdgeLeaderType(edge: Edge): NormalizedEdge {
    return {
      ...edge,
      leaderType: edge.leaderType ?? ents.extra.common.LeaderType.Manager,
    }
  }

  /** Normalizes delta edge changes with a concrete leader type. */
  function normalizeEdgeChangeLeaderType(
    edgeChange: EdgeChange,
  ): NormalizedEdgeChange {
    return {
      ...edgeChange,
      leaderType: edgeChange.leaderType ?? ents.extra.common.LeaderType.Manager,
    }
  }

  /**
   * Produces the semantic identity for one child's typed leader relationship.
   */
  function edgeRelationshipKey(edge: {
    childId: ents.core.display.tree.Node.Id
    leaderType?: ents.extra.common.LeaderType
  }): string {
    return `${edge.childId}::${
      edge.leaderType ?? ents.extra.common.LeaderType.Manager
    }`
  }
}
